<template>
  <div>
    <!-- API Navigation Component -->
    <api-navigation :anchors="anchors" />

    <!-- API Documentation Content -->
    <div ref="documentationContentEl" class="api-documentation documentation">
      <div class="background">
        <div class="left" />
        <div class="right" />
      </div>
      <div class="foreground">
        <!-- API Info Section -->
        <api-info-section :info="document.info" />
        <!-- API Spec Download Section -->
        <api-spec-download-section :id="id" />
        <!-- API Tag Sections -->
        <api-tag-section v-for="tag in document.tagWithOperations" :key="tag.name" :tag="tag" />
        <!-- API Webhooks Section -->
        <api-webhooks-section v-if="document.webhooks" :document="document" />
        <!-- API Document Errors Sections -->
        <api-document-errors-sections v-if="document.errors" :id="id" :document="document" />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { nextTick, onMounted, PropType, ref } from 'vue';
import { ApiDoc } from '@bifrost-dev-docs-mfe/common/types/apiDocs';
import ApiTagSection from '@/components/api/content/ApiTagSection.vue';
import ApiSpecDownloadSection from '@/components/api/content/ApiSpecDownloadSection.vue';
import ApiInfoSection from '@/components/api/content/ApiInfoSection.vue';
import ApiNavigation from '@/components/api/navigation/ApiNavigation.vue';
import ApiWebhooksSection from '@/components/api/content/ApiWebhooksSection.vue';
import ApiDocumentErrorsSections from '@/components/api/content/ApiDocumentErrorsSections.vue';

defineProps({
  id: { type: String, required: true },
  document: { type: Object as PropType<ApiDoc>, required: true },
});
const anchors = ref([] as Element[]);
const documentationContentEl = ref(null as HTMLElement | null);

/**
 * Only h1,h2 has been taken for navigation to match with old website.
 * */
onMounted(async () => {
  await nextTick();
  if (documentationContentEl.value) {
    anchors.value = Array.from(documentationContentEl.value.querySelectorAll('h1,h2')).filter(
      el => el.id && el.textContent
    );
  }
});
</script>

<style lang="scss">
.api-documentation {
  height: 100%;
  padding: 0 32px 48px 32px !important;
  overflow-x: auto;

  a {
    color: var(--vvd-color-cta-700);
  }

  pre,
  code,
  kbd,
  samp,
  var {
    padding: 3px 4px;
    border-radius: 6px;
  }

  code,
  kbd,
  samp,
  var {
    background-color: #f2f2f2;
    color: #bb3700;
  }

  @media screen and (max-width: $mobile-large) {
    padding: 16px 32px 48px 32px !important;
  }

  .background {
    position: fixed;
    left: 0;
    top: 0;
    right: 0;
    bottom: 0;
    z-index: -1000;

    display: flex;
    div {
      flex-grow: 1;

      &.right {
        background-color: hsla(218, 33%, 94%, 1);
      }
    }
  }

  .foreground {
    display: flex;
    flex-direction: column;
    z-index: 1000;
    padding: 32px 0 60px 0;

    .row {
      display: flex;
      flex-direction: row;
      flex-wrap: wrap;

      & > div {
        flex-basis: 50%;
        max-width: 50%;
        padding: 0 36px;
        box-sizing: border-box;

        &:nth-of-type(2) {
          h1,
          h2,
          h3,
          h4 {
            margin-left: 0;
          }
        }
      }
    }
  }

  h1 {
    margin-bottom: 8px;
  }

  h4 {
    padding-top: 24px;
    margin-bottom: 16px;
  }

  .pill {
    text-transform: uppercase;
    padding: 2px 4px;
    border-radius: 4px;
    font-weight: 400;
    letter-spacing: normal;

    &.small {
      font-size: 8px;
      line-height: 12px;
    }

    &.medium {
      font-size: 10px;
      line-height: 14px;
    }

    &.large {
      font-size: 12px;
      line-height: 16px;
    }

    &.title-badge {
      background-color: var(--vvd-color-cta-700);
      color: var(--vvd-color-canvas);
      margin-left: 16px;
    }
  }

  table {
    margin: 16px 0;
    p {
      margin: 0 !important;
    }
    th,
    td {
      padding: 12px 16px;
      text-align: start;
    }
    tr {
      border-bottom-width: 1px;
      border-bottom-style: solid;
      block-size: 44px;
      border-bottom-color: var(--vvd-color-neutral-100);
    }
    thead {
      letter-spacing: 0;
      text-decoration: none;
      text-transform: none;
      tr {
        border-bottom-color: var(--vvd-color-canvas-text);
      }
    }
  }
}

@media screen and (max-width: $laptop) {
  .api-documentation {
    .background {
      .right {
        display: none;
      }
    }
    .foreground {
      .row {
        & > div {
          flex-basis: 100%;
          max-width: 100%;

          &:nth-of-type(2) {
            display: none;
          }
        }
      }
    }
  }
}

@media screen and (max-width: $mobile-large) {
  .api-documentation {
    .background {
      .right {
        display: none;
      }
    }
    .foreground {
      .row {
        & > div {
          padding: 0 16px;
        }
      }
    }
  }
}

.navigation-section {
  top: var(--bifrost-header-height);
  position: sticky;

  display: flex;
  gap: 10px;
  background-color: hsl(218deg, 33%, 94%);
  padding: 10px;
  overflow-x: auto;
  max-height: 168px;
  z-index: 1;
}
</style>
